import { useSyncExternalStore } from "react";
import {
	isCustomerFacingAppPath,
	isDashboardSurface,
	isStatusPage,
	isThankYouPage,
} from "./surface";

/**
 * Dashboard colour theme.
 *
 * The creator's choice is "system" (follow the OS), "light" or "dark". It is
 * resolved to a concrete theme and written to `<html data-theme>`, which drives
 * every `--pf-*` token in the design-system tokens.css.
 *
 * Where the choice lives (POC-958):
 * - On the account (`User.theme`, PUT /users/theme) — the source of truth, so
 *   the theme follows the creator to every browser and device.
 * - In localStorage (`pf-theme`) — a per-browser copy the pre-paint script in
 *   index.html reads, so the very first frame is already in the right palette.
 * When `/users/me` arrives, `connectThemeAccount()` reconciles the two before
 * the dashboard paints. A change is applied and stored locally at once, then
 * saved to the account; until the save lands, `pf-theme-pending` (holding the
 * user id) makes the next load re-send it instead of reverting it.
 *
 * Dark is for the dashboard, our own public status page (POC-971) and the
 * buyer's thank-you page after checkout (POC-975). The last two have their
 * own System / Light / Dark switcher and share this browser's `pf-theme`
 * choice. Everything else a buyer can see — storefronts, checkout, the
 * subscription portal, invoices — is always light: those pages carry
 * creator-chosen colours, not ours. The inline pre-paint script in index.html
 * applies the same rule before first paint so there is no white flash;
 * `initTheme()` re-applies it as the source of truth.
 *
 * The default creator page (a store with no designed Creator Page) is themed
 * too: it carries only our tokens. It shares `/` with designed pages, which
 * stay light, so neither the path rule nor the pre-paint script can tell
 * them apart; `Sales/Creator/Creator.tsx` calls `initTheme(true)` once it
 * knows it is rendering the default page.
 */

export type ThemePreference = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "pf-theme";
/** The user id whose latest choice has not reached the account yet. */
export const THEME_PENDING_KEY = "pf-theme-pending";
export const THEME_PREFERENCES: readonly ThemePreference[] = [
	"system",
	"light",
	"dark",
];

/** Browser chrome colour per theme — mirrors `--pf-bg` in tokens.css. */
export const THEME_COLOR: Record<ResolvedTheme, string> = {
	light: "#ffffff",
	dark: "#0a0a0a",
};

const DARK_QUERY = "(prefers-color-scheme: dark)";

const isPreference = (value: unknown): value is ThemePreference =>
	value === "system" || value === "light" || value === "dark";

/** The choice saved in this browser, or null if this browser never had one. */
const readStoredPreference = (): ThemePreference | null => {
	try {
		const stored = localStorage.getItem(THEME_STORAGE_KEY);
		return isPreference(stored) ? stored : null;
	} catch {
		return null;
	}
};

export const readThemePreference = (): ThemePreference =>
	readStoredPreference() ?? "system";

const storage = {
	get(key: string): string | null {
		try {
			return localStorage.getItem(key);
		} catch {
			return null;
		}
	},
	set(key: string, value: string) {
		try {
			localStorage.setItem(key, value);
		} catch {
			// Storage blocked: the choice still applies for this page load.
		}
	},
	remove(key: string) {
		try {
			localStorage.removeItem(key);
		} catch {
			// ignore
		}
	},
};

const systemPrefersDark = (): boolean => {
	try {
		return window.matchMedia?.(DARK_QUERY).matches ?? false;
	} catch {
		return false;
	}
};

export const resolveTheme = (
	preference: ThemePreference,
	prefersDark: boolean,
): ResolvedTheme => {
	if (preference === "system") return prefersDark ? "dark" : "light";
	return preference;
};

/**
 * Whether this page load may render dark at all: the creator dashboard, the
 * public status page and the thank-you page, never another buyer-facing page
 * (see the module comment).
 */
export const canUseDarkTheme = (
	hostname = window.location.hostname,
	pathname = window.location.pathname,
): boolean =>
	isStatusPage(pathname) ||
	isThankYouPage(pathname) ||
	(isDashboardSurface(hostname, pathname) &&
		!isCustomerFacingAppPath(pathname));

type Snapshot = { preference: ThemePreference; resolved: ResolvedTheme };

let enabled = false;
let initialised = false;
let snapshot: Snapshot = { preference: "system", resolved: "light" };
const listeners = new Set<() => void>();

const applyTheme = (theme: ResolvedTheme) => {
	const root = document.documentElement;
	root.dataset.theme = theme;
	root.style.colorScheme = theme;
	// Buyer pages keep the storefront's own browser-chrome colour.
	if (!enabled) return;
	const meta = document.querySelector<HTMLMetaElement>(
		'meta[name="theme-color"]',
	);
	if (meta) meta.content = THEME_COLOR[theme];
};

/**
 * Swap the palette in one frame. Many controls transition their colours on
 * hover; without this they would all fade at different speeds on a switch.
 * (Same technique as next-themes' disableTransitionOnChange.)
 */
const withoutTransitions = (apply: () => void) => {
	const style = document.createElement("style");
	style.textContent =
		"*,*::before,*::after{transition:none!important;animation-duration:0s!important}";
	document.head.appendChild(style);
	apply();
	// Force a style flush so the new palette lands while transitions are off.
	void getComputedStyle(document.body).backgroundColor;
	requestAnimationFrame(() => requestAnimationFrame(() => style.remove()));
};

const recompute = () => {
	const preference = readThemePreference();
	const resolved = enabled
		? resolveTheme(preference, systemPrefersDark())
		: "light";
	if (
		snapshot.preference !== preference ||
		snapshot.resolved !== resolved ||
		document.documentElement.dataset.theme !== resolved
	) {
		const first = !initialised;
		snapshot = { preference, resolved };
		if (first) applyTheme(resolved);
		else withoutTransitions(() => applyTheme(resolved));
		for (const listener of listeners) listener();
	}
};

/**
 * Apply the theme for this page load and keep it in sync with the OS setting
 * and with other tabs. Call once, before React renders.
 */
export const initTheme = (allowDark = canUseDarkTheme()): void => {
	enabled = allowDark;
	recompute();
	if (initialised) return;
	initialised = true;
	try {
		window.matchMedia?.(DARK_QUERY).addEventListener?.("change", recompute);
	} catch {
		// Old Safari without addEventListener on MediaQueryList: the OS change
		// is picked up on the next load instead.
	}
	window.addEventListener("storage", (event) => {
		if (event.key === THEME_STORAGE_KEY) recompute();
	});
};

/** Store locally and repaint — no account write. */
const commitPreference = (preference: ThemePreference) => {
	storage.set(THEME_STORAGE_KEY, preference);
	const resolved = enabled
		? resolveTheme(preference, systemPrefersDark())
		: "light";
	const changed =
		snapshot.preference !== preference || snapshot.resolved !== resolved;
	snapshot = { preference, resolved };
	if (document.documentElement.dataset.theme !== resolved) {
		withoutTransitions(() => applyTheme(resolved));
	}
	if (changed) for (const listener of listeners) listener();
};

// ---- Account sync ---------------------------------------------------------

export type ThemeSaver = (preference: ThemePreference) => Promise<unknown>;

let account: { userId: string; save: ThemeSaver } | null = null;
// Saves run one at a time, in click order, so a slow request can never land
// after a newer one and leave the account on an older choice.
let saveQueue: Promise<unknown> = Promise.resolve();
let latestSave = 0;

// The choice currently waiting in (or travelling through) the queue, so a
// reconcile that runs again meanwhile (StrictMode, a remounted guard, a
// background /users/me refetch) does not send the same value twice.
let queued: { userId: string; preference: ThemePreference } | null = null;

const pushToAccount = (preference: ThemePreference) => {
	if (!account) return;
	const { userId, save } = account;
	storage.set(THEME_PENDING_KEY, userId);
	if (queued?.userId === userId && queued.preference === preference) return;
	queued = { userId, preference };
	const ticket = ++latestSave;
	saveQueue = saveQueue
		.then(() => save(preference))
		.then(
			() => {
				// Only the newest save may clear the marker.
				if (
					ticket === latestSave &&
					storage.get(THEME_PENDING_KEY) === userId
				) {
					storage.remove(THEME_PENDING_KEY);
				}
			},
			() => {
				// Leave the marker: the next load re-sends this browser's choice.
			},
		)
		.finally(() => {
			if (ticket === latestSave) queued = null;
		});
};

/**
 * Reconcile this browser with the signed-in account. Call as soon as
 * `/users/me` is known (before the dashboard paints), and again whenever it
 * changes.
 *
 * - A local change that never reached this account is re-sent.
 * - Otherwise the account's theme wins, and is copied into localStorage.
 * - An account with no theme yet adopts this browser's choice, if it has one.
 */
export const connectThemeAccount = (
	userId: string,
	accountTheme: unknown,
	save: ThemeSaver,
): void => {
	account = { userId, save };
	const local = readStoredPreference();

	if (storage.get(THEME_PENDING_KEY) === userId) {
		pushToAccount(local ?? "system");
		return;
	}
	if (isPreference(accountTheme)) {
		if (accountTheme !== local) commitPreference(accountTheme);
		return;
	}
	if (local) pushToAccount(local);
};

/** A choice made in the UI: apply now, keep locally, save to the account. */
export const setThemePreference = (preference: ThemePreference): void => {
	commitPreference(preference);
	pushToAccount(preference);
};

const subscribe = (listener: () => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

const getSnapshot = () => snapshot;

export const useTheme = (): Snapshot & {
	setPreference: (preference: ThemePreference) => void;
} => {
	const current = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
	return { ...current, setPreference: setThemePreference };
};

/** Test-only: reset module state between cases. */
export const __resetThemeForTests = (): void => {
	account = null;
	queued = null;
	saveQueue = Promise.resolve();
	latestSave = 0;
	enabled = false;
	initialised = false;
	snapshot = { preference: "system", resolved: "light" };
	listeners.clear();
};
